import * as Sentry from '@sentry/react';
import {Log as OidcLogger} from 'oidc-client-ts';
import {Configuration} from 'configuration';
import type {Indexer} from '../types';
import type {Log, LogError} from './types';

export const initLogging = () => {
  if (Configuration.sentry.enabled) {
    Sentry.init({
      dsn: Configuration.sentry.dsn,
      environment: Configuration.env,
      integrations: [
        Sentry.browserTracingIntegration({
          beforeStartSpan: context => {
            return {
              ...context,
              // Can even look at context tags or other data to adjust
              // transaction name
              name: window.location.pathname, //  getTransactionName(window.location),
            };
          },
        }),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
          networkDetailAllowUrls: [
            window.location.origin,
            Configuration.api.baseUri,
          ],
          mutationLimit: 2000, // Default 10000
        }),
        Sentry.replayCanvasIntegration(),
        Sentry.featureFlagsIntegration(),
        Sentry.consoleLoggingIntegration({levels: ['log', 'warn', 'error']}),
      ],
      // Replay settings
      tracesSampleRate: Configuration.env === 'prod' ? 0.2 : 1.0,
      replaysSessionSampleRate: Configuration.env === 'prod' ? 0.2 : 1.0,
      replaysOnErrorSampleRate: Configuration.env === 'prod' ? 1 : 0,

      // Logs settings
      enableLogs: true,

      beforeSend(event) {
        // Modify the event here
        event.message = event.message ?? event.exception?.values?.[0]?.value;
        return event;
      },
    });

    OidcLogger.setLogger(Sentry.logger);
    OidcLogger.setLevel(OidcLogger.WARN);
  } else {
    OidcLogger.setLogger(console);
    OidcLogger.setLevel(OidcLogger.INFO);
  }
};

export const logError: LogError = (error: Error | string, data?: Indexer) => {
  if (Configuration.sentry.enabled) {
    Sentry.withScope(scope => {
      data && scope.setExtras(data);

      if (typeof error === 'string') {
        Sentry.captureMessage(error, 'error');
      } else {
        Sentry.captureException(error);
      }
    });
  } else {
    console.error(error, data);
  }
};

export const logWarning: Log = (message: string, data?: Indexer) => {
  if (Configuration.sentry.enabled) {
    Sentry.withScope(scope => {
      data && scope.setExtras(data);

      Sentry.captureMessage(message, 'warning');
    });
  } else {
    console.warn(message, data);
  }
};
